import Coin from '../../Components/Coin';
import EmoteIcon from '../../Components/EmoteIcon';
import Gem from '../../Components/Gem';
import Modal from '../../Components/Modal';
import ProfileAvatar from '../../Components/ProfileAvatar';
import BkButton from '../../Components/ui/BkButton';
import { api } from 'aws-blocks';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import AgeConfirm from '../../Components/AgeConfirm';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import useApiAction from '../../hooks/use-api-action';
import {
  PLAYER_SKINS,
  getPlayerSkinDef,
  skinSwatchBackground,
} from '../../player-colors';
import { canUseAvatar } from '../../profile-avatars/registry';
import { canUseEmote } from '../../emotes/registry';
import { formatCurrencyAmount } from '../../utils/format-currency';
import type { StoreItem } from './StoreItemTile';
import { rarityClass } from './StoreItemTile';

export type StoreItemModalProps = {
  selectedItem?: StoreItem;
  onClose: () => void;
};

function isSkinCategory(category?: string) {
  return category === 'skins' || category === 'colors';
}

export default function StoreItemModal({
  selectedItem,
  onClose,
}: StoreItemModalProps) {
  const {
    itemId,
    title,
    comingSoon,
    description,
    imageURL,
    backgroundColor = '#151c2e',
    money,
    gems,
    coins,
    priceCoins,
    priceGems,
    grantGems,
    category,
    colorId,
    skinId,
    iconId,
    emoteId,
    fill,
    rarity,
    passExclusive,
  } = selectedItem ?? {};

  const buyCoins = Number(priceCoins ?? coins ?? 0);
  const buyGems = Number(priceGems ?? gems ?? 0);
  const buyMoney = Number(money ?? 0);
  const isGemPack = category === 'gems' && buyMoney > 0;
  const isIconItem = category === 'icons';
  const isEmoteItem = category === 'emotes';
  const resolvedIconId = String(iconId ?? '');
  const resolvedEmoteId = String(emoteId ?? '');

  const {
    currentUser,
    userData: {
      coins: userCoins = 0,
      gems: userGems = 0,
      ownedColorIds,
      ownedIconIds,
      ownedEmoteIds,
      equippedColorId,
      altSkinId,
      avatarId,
      featuredEmoteId,
    } = {},
    openLoginModal,
  } = useAuthContext();

  const { addSnackBar } = useSnackBarContext();
  const [confirm18, setConfirm18] = useState(false);
  const resolvedSkinId = String(skinId ?? colorId ?? itemId ?? '');
  const isSkinItem = isSkinCategory(String(category));
  const previewSwatch = isSkinItem
    ? skinSwatchBackground(getPlayerSkinDef(resolvedSkinId))
    : String(fill || backgroundColor);
  const defaultIds = PLAYER_SKINS.filter((c) => c.isDefault).map((c) => c.id);
  const ownedSkins = [
    ...new Set([
      ...defaultIds,
      ...(Array.isArray(ownedColorIds) ? ownedColorIds.map(String) : []),
    ]),
  ];
  const alreadyOwnedSkin = isSkinItem && ownedSkins.includes(resolvedSkinId);
  const alreadyOwnedIcon =
    isIconItem &&
    canUseAvatar(resolvedIconId, ownedColorIds, ownedIconIds);
  const alreadyOwnedEmote =
    isEmoteItem &&
    canUseEmote(resolvedEmoteId, ownedColorIds, ownedEmoteIds);
  const isPrimary = isSkinItem && equippedColorId === resolvedSkinId;
  const isAlt = isSkinItem && altSkinId === resolvedSkinId;
  const isFreeSkin = isSkinItem && !buyCoins && !buyGems;
  const isEquippedIcon = isIconItem && avatarId === resolvedIconId;
  const isFeaturedEmote = isEmoteItem && featuredEmoteId === resolvedEmoteId;

  useEffect(() => {
    setConfirm18(false);
  }, [itemId]);

  const [purchaseItem, loadingPurchase] = useApiAction({
    action: async (payload: { purchaseType?: string }) =>
      api.purchaseItem(String(itemId), String(payload.purchaseType)),
    reloadUserData: true,
  });

  const [createGemCheckout, loadingCheckout] = useApiAction({
    action: async () =>
      api.createGemCheckout(String(itemId)) as Promise<{ url: string }>,
  });

  const [equipSkin, loadingEquip] = useApiAction({
    action: async (payload: { slot: 'primary' | 'alt' }) =>
      api.equipSkin(resolvedSkinId, payload.slot),
    reloadUserData: true,
  });

  const [clearAlt, loadingClear] = useApiAction({
    action: async () => api.clearAltSkin(),
    reloadUserData: true,
  });

  const [equipIcon, loadingIcon] = useApiAction({
    action: async () => api.updateAvatar(resolvedIconId),
    reloadUserData: true,
  });

  const [equipEmote, loadingEmote] = useApiAction({
    action: async () => api.updateFeaturedEmote(resolvedEmoteId),
    reloadUserData: true,
  });

  const loading =
    loadingPurchase ||
    loadingCheckout ||
    loadingEquip ||
    loadingClear ||
    loadingIcon ||
    loadingEmote;

  async function handlePurchase({ purchaseType }: { purchaseType: string }) {
    if (purchaseType === 'gems' && buyGems > Number(userGems)) {
      addSnackBar({
        title: 'ERROR',
        message: 'You do not have enough gems to purchase this item.',
        variant: 'danger',
        timeout: 2500,
      });
      return;
    }
    if (purchaseType === 'coins' && buyCoins > Number(userCoins)) {
      addSnackBar({
        title: 'ERROR',
        message: 'You do not have enough coins to purchase this item.',
        variant: 'danger',
        timeout: 2500,
      });
      return;
    }
    await purchaseItem({ purchaseType });
    addSnackBar({
      title: 'Purchased',
      message: `${title ?? 'Item'} unlocked.`,
      variant: 'success',
      timeout: 2000,
    });
    onClose();
  }

  async function handleStripeCheckout() {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    if (!confirm18) {
      addSnackBar({
        title: 'Age required',
        message: 'Confirm you are 18 or older before paying with a card.',
        variant: 'warning',
        timeout: 2500,
      });
      return;
    }
    const result = await createGemCheckout();
    if (result?.url) {
      window.location.assign(result.url);
    }
  }

  async function handleEquipPrimary() {
    await equipSkin({ slot: 'primary' });
    addSnackBar({
      title: 'Equipped',
      message: `${title ?? 'Skin'} is now your primary skin.`,
      variant: 'success',
      timeout: 2000,
    });
    onClose();
  }

  async function handleEquipAlt() {
    try {
      await equipSkin({ slot: 'alt' });
      addSnackBar({
        title: 'Alt Equipped',
        message: `${title ?? 'Skin'} is now your alt skin.`,
        variant: 'success',
        timeout: 2000,
      });
      onClose();
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  async function handleClearAlt() {
    await clearAlt();
    addSnackBar({
      title: 'Alt Cleared',
      message: 'Your alt skin was removed.',
      variant: 'success',
      timeout: 2000,
    });
    onClose();
  }

  async function handleEquipIcon() {
    await equipIcon();
    addSnackBar({
      title: 'Portrait set',
      message: `${title ?? 'Icon'} is now your profile portrait.`,
      variant: 'success',
      timeout: 2000,
    });
    onClose();
  }

  async function handleFeatureEmote() {
    await equipEmote();
    addSnackBar({
      title: 'Featured',
      message: `${title ?? 'Emote'} is featured on your profile.`,
      variant: 'success',
      timeout: 2000,
    });
    onClose();
  }

  return (
    <Modal show={!!selectedItem} onClose={onClose} title={String(title || 'Item')}>
      <div className="space-y-3 p-2">
        {(imageURL ||
          fill ||
          backgroundColor ||
          isSkinItem ||
          isGemPack ||
          isIconItem ||
          isEmoteItem) && (
          <div
            className="relative flex h-28 items-center justify-center overflow-hidden border border-bk-border"
            style={{
              background: isIconItem || isEmoteItem
                ? `radial-gradient(circle at 35% 30%, ${backgroundColor}88, #0a0e17 75%)`
                : previewSwatch,
            }}
          >
            {imageURL ? (
              <img src={String(imageURL)} alt="" className="mx-auto max-h-32" />
            ) : isGemPack ? (
              <div className="scale-[2.4]">
                <Gem />
              </div>
            ) : isIconItem ? (
              <ProfileAvatar avatarId={resolvedIconId} size="md" />
            ) : isEmoteItem ? (
              <EmoteIcon emoteId={resolvedEmoteId} size="lg" />
            ) : (
              <div
                className="h-14 w-14 rounded-full border-4 border-black/40 shadow-lg"
                style={{ background: previewSwatch }}
              />
            )}
            {rarity ? (
              <span
                className={`absolute right-2 top-2 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${rarityClass(String(rarity))}`}
              >
                {rarity}
              </span>
            ) : null}
          </div>
        )}
        <p className="text-sm text-bk-muted">{description}</p>
        {isGemPack && Number(grantGems ?? 0) > 0 ? (
          <p className="text-sm text-fuchsia-300 tabular-nums">
            Grants {formatCurrencyAmount(Number(grantGems))} gems
          </p>
        ) : null}

        {passExclusive ? (
          <div className="flex flex-col items-center gap-3">
            <p className="text-center text-sm text-bk-muted">
              This cosmetic is exclusive to the Season 1 Battle Pass.
            </p>
            <Link to="/pass" onClick={onClose}>
              <BkButton variant="primary" size="sm">
                Open Battle Pass
              </BkButton>
            </Link>
          </div>
        ) : comingSoon ? (
          <p className="text-center text-sm font-semibold uppercase tracking-wide text-bk-gold">
            Coming soon
          </p>
        ) : isSkinItem && (alreadyOwnedSkin || isFreeSkin) ? (
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-wrap justify-center gap-2">
              <BkButton
                variant="primary"
                size="sm"
                disabled={!currentUser || loading || isPrimary}
                onClick={() => void handleEquipPrimary()}
              >
                {isPrimary ? 'Primary' : 'Equip Primary'}
              </BkButton>
              <BkButton
                variant="secondary"
                size="sm"
                disabled={!currentUser || loading || isPrimary || isAlt}
                onClick={() => void handleEquipAlt()}
              >
                {isAlt ? 'Alt Equipped' : 'Equip as Alt'}
              </BkButton>
            </div>
            {isAlt ? (
              <BkButton
                variant="ghost"
                size="sm"
                disabled={!currentUser || loading}
                onClick={() => void handleClearAlt()}
              >
                Clear Alt
              </BkButton>
            ) : null}
            <p className="text-center text-xs text-bk-muted">
              Alt is Player 2 in local games, and a fallback when another player
              has the same primary online.
            </p>
          </div>
        ) : isIconItem && alreadyOwnedIcon ? (
          <div className="flex flex-col items-center gap-2">
            <BkButton
              variant="primary"
              size="sm"
              disabled={!currentUser || loading || isEquippedIcon}
              onClick={() => void handleEquipIcon()}
            >
              {isEquippedIcon ? 'Equipped' : 'Equip portrait'}
            </BkButton>
          </div>
        ) : isEmoteItem && alreadyOwnedEmote ? (
          <div className="flex flex-col items-center gap-2">
            <BkButton
              variant="primary"
              size="sm"
              disabled={!currentUser || loading || isFeaturedEmote}
              onClick={() => void handleFeatureEmote()}
            >
              {isFeaturedEmote ? 'Featured' : 'Feature on profile'}
            </BkButton>
            <p className="text-center text-xs text-bk-muted">
              Manage your in-match loadout on your profile Emotes tab.
            </p>
          </div>
        ) : isGemPack ? (
          <div className="flex flex-col items-center gap-2">
            <AgeConfirm
              id="gem-pack-age-18"
              checked={confirm18}
              onChange={setConfirm18}
            >
              I confirm I am 18 or older and I am buying this for myself.
            </AgeConfirm>
            <BkButton
              variant="primary"
              size="sm"
              disabled={loading || (Boolean(currentUser) && !confirm18)}
              onClick={() => void handleStripeCheckout()}
              className="gap-2"
            >
              {currentUser
                ? `Buy ${buyMoney.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}`
                : 'Log in to buy'}
            </BkButton>
            <p className="text-center text-xs text-bk-muted">
              Secure checkout powered by Stripe. Purchases are subject to our{' '}
              <Link
                className="text-bk-gold underline-offset-2 hover:underline"
                to="/terms#purchases"
                onClick={onClose}
              >
                Terms
              </Link>
              .
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-2">
            {!!buyCoins && (
              <BkButton
                variant="primary"
                size="sm"
                disabled={!currentUser || loading}
                onClick={() => void handlePurchase({ purchaseType: 'coins' })}
                className="gap-2 normal-case tabular-nums"
              >
                Buy <Coin /> {formatCurrencyAmount(buyCoins)}
              </BkButton>
            )}
            {!!buyGems && (
              <BkButton
                variant="secondary"
                size="sm"
                disabled={!currentUser || loading}
                onClick={() => void handlePurchase({ purchaseType: 'gems' })}
                className="gap-2 normal-case tabular-nums"
              >
                Buy <Gem /> {formatCurrencyAmount(buyGems)}
              </BkButton>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
